import { Suspense, lazy } from 'react';
import { Route, Routes } from 'react-router-dom';
import DashboardLayout from '@/layouts/DashboardLayout';
import { Route as RouteType } from '@sm-dashboard/types';
import Intro from '@/pages/auth/Intro';
import Login from '@/pages/auth/Login';
import Report from '@/pages/auth/Report';
import RegisterForm from '@/pages/auth/Register';
import { Box, CircularProgress } from '@mui/material';
import usePageTracking from '@/hooks/usePageTracking';
import TitleUpdater from '@/components/common/TitleUpdater';
import { syncMissingRoutePrimaries } from './routesSync';
import { pagesList } from '@/constants';
import SnackbarWarper from '@/components/common/SnackbarWarper/SnackbarWarper';

const LeadsPage = lazy(() => import('@/pages/dashboard/Leads'));
const ShalhavotLeadsPage = lazy(() => import('@/pages/dashboard/Shalhavot/LeadsTable'));
const ShalhavotDashboard = lazy(() => import('@/pages/dashboard/Shalhavot/Dashboard'));
const KindergartenDashboard = lazy(() => import('@/pages/dashboard/Kindergarten/Dashboard'));
const DaycareCentersLeadsPage = lazy(() => import('@/pages/dashboard/DaycareCenters/LeadsTable'));
const DaycareCentersDashboard = lazy(() => import('@/pages/dashboard/DaycareCenters/Dashboard'));
const Guid = lazy(() => import('@/pages/dashboard/Guid'));
const StaffMembers = lazy(() => import('@/pages/dashboard/StaffMembers'));
const Feed = lazy(() => import('@/pages/dashboard/Feed'));
const Details = lazy(() => import('@/pages/dashboard/Details/Details'));
const SettingsPage = lazy(() => import('@/pages/dashboard/Settings'));
const Tags = lazy(() => import('@/pages/dashboard/Tags'));
const Institutions = lazy(() => import('@/pages/dashboard/Institutions'));
const InstitutionDetails = lazy(() => import('@/pages/dashboard/InstitutionDetails'));
const FormsPage = lazy(() => import('@/pages/dashboard/Forms'));
const FormResultsPage = lazy(() => import('@/pages/dashboard/FormResults'));
const PublicFormPage = lazy(() => import('@/pages/dashboard/PublicForm'));
const UploadReferencePage = lazy(() => import('@/pages/dashboard/UploadReference'));
const LinksPage = lazy(() => import('@/pages/dashboard/Links'));
const StaffMembersBook = lazy(() => import('@/pages/dashboard/StaffMembersBook'));
const LeadsRedirect = lazy(() => import('@/components/redirects/LeadsRedirect'));
const ShufraShalhavot = lazy(() => import('@/pages/dashboard/ShufraShalhavot'));
const ShufraKindergartens = lazy(() => import('@/pages/dashboard/ShufraKindergartens'));
const ShufraSchools = lazy(() => import('@/pages/dashboard/ShufraSchools'));
const GrantFundPage = lazy(() => import('@/pages/dashboard/GrantFund/GrantFundPage'));
const GrantFundManagerPage = lazy(() => import('@/pages/dashboard/GrantFund/GrantFundManagerPage'));
const GrantFundDashboardPage = lazy(() => import('@/pages/dashboard/GrantFund/GrantFundDashboardPage'));
const LoansPage = lazy(() => import('@/pages/dashboard/Loans/LoansPage'));
const LoansManagerPage = lazy(() => import('@/pages/dashboard/Loans/LoansManagerPage'));
const SupportsPage = lazy(() => import('@/pages/dashboard/Supports/SupportsPage'));
const SupportsManagerPage = lazy(() => import('@/pages/dashboard/Supports/SupportsManagerPage'));

const routesList: RouteType[] = [
  { route: '/', element: <Intro />, primary: 'נתיב ראשי לדף הבית', isPublic: true },
  { route: '/intro', element: <Intro />, primary: 'כניסה', isPublic: true },
  { route: '/login', element: <Login />, primary: 'התחברות', isPublic: true },
  { route: '/register', element: <RegisterForm />, primary: 'הרשמה', isPublic: true },
  { route: '/report', element: <Report />, primary: 'דיווח על בעיה', isPublic: true },
  { route: '/details/:id', element: <Details />, primary: 'הפרטים שלי - לפי שליח' },
  { route: '/details', element: <Details />, primary: 'הפרטים שלי', isPublic: true },
  { route: '/kindergarten-leads', element: <LeadsPage />, primary: 'רישום גנים' },
  { route: '/leads', element: <LeadsRedirect />, primary: 'רישום גנים (מחובר מחדש)' }, // Redirect for backwards compatibility
  { route: '/shalhavot-leads', element: <ShalhavotLeadsPage />, primary: 'רישום שלהבות' },
  { route: '/shalhavot-dashboard', element: <ShalhavotDashboard />, primary: 'לוח בקרה שלהבות' },
  { route: '/kindergarten-dashboard', element: <KindergartenDashboard />, primary: 'לוח בקרה גנים' },
  { route: '/daycare-centers-leads', element: <DaycareCentersLeadsPage />, primary: 'רישום מעונות' },
  { route: '/daycare-centers-dashboard', element: <DaycareCentersDashboard />, primary: 'לוח בקרה מעונות' },
  { route: '/settings', element: <SettingsPage />, primary: 'הגדרות' },
  { route: '/forms', element: <FormsPage />, primary: 'טפסים' },
  { route: '/forms/results/:formId', element: <FormResultsPage />, primary: 'טבלת תוצאות טפסים' },
  { route: '/public-form/:formId', element: <PublicFormPage />, primary: 'טופס ציבורי - לפי מזהה', isPublic: true },
  { route: '/upload-reference', element: <UploadReferencePage />, primary: 'העלאת אישור רישום', isPublic: true },
  { route: '/links', element: <LinksPage />, primary: 'קישורים' },
  { route: '/staff-members-book', element: <StaffMembersBook />, primary: 'ספר מורים' },
  { route: '/users', element: <StaffMembers />, primary: 'משתמשים' },
  { route: '/guid', element: <Guid />, primary: 'מדריך למשתמש' },
  { route: '/feed/:postId', element: <Feed />, primary: 'חדשות - לפי פוסט' },
  { route: '/feed', element: <Feed />, primary: 'חדשות' },
  { route: '/tags', element: <Tags />, primary: 'ניהול תגיות' },
  { route: '/institutions-list', element: <Institutions />, primary: 'ספר המוסדות' },
  { route: '/institutions/:id', element: <InstitutionDetails />, primary: 'פרטי מוסד' },
  { route: '/institutions', element: <Institutions />, primary: 'מוסדות' },
  { route: '/shufra/shalhavot', element: <ShufraShalhavot />, primary: 'שופרא - שלהבות' },
  { route: '/shufra/kindergartens', element: <ShufraKindergartens />, primary: 'שופרא - גנים' },
  { route: '/shufra/schools', element: <ShufraSchools />, primary: 'שופרא - בתי ספר' },
  { route: '/grant-fund', element: <GrantFundPage />, primary: 'בקשות במעגל השנה' },
  {
    route: '/grant-fund/system',
    element: <GrantFundManagerPage />,
    primary: 'בקשות במעגל השנה - מערכת מנהלים'
  },
  {
    route: '/grant-fund/dashboard',
    element: <GrantFundDashboardPage />,
    primary: 'לוח בקרה בקשות במעגל השנה'
  },
  { route: '/loans', element: <LoansPage />, primary: 'הלוואות' },
  {
    route: '/loans/system',
    element: <LoansManagerPage />,
    primary: 'הלוואות - מערכת מנהלים'
  },
  { route: '/supports', element: <SupportsPage />, primary: 'תמיכות' },
  {
    route: '/supports/system',
    element: <SupportsManagerPage />,
    primary: 'תמיכות - מערכת מנהלים'
  }
];

// Sync routes with pagesList to ensure all primaries match
export const routes = syncMissingRoutePrimaries(routesList, pagesList);

// // Check for missing routes during development
// if (process.env.NODE_ENV === 'development') {
//   findMissingRoutes(routesList, pagesList);
// }

const App = () => {
  usePageTracking();

  return (
    <Suspense
      fallback={
        <DashboardLayout>
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', width: '100%', height: '100%' }}>
            <CircularProgress />
          </Box>
        </DashboardLayout>
      }
    >
      <TitleUpdater />
      <Routes>
        {routes.map(route => (
          <Route key={route.route} path={route.route} element={route.element} />
        ))}
      </Routes>
      <SnackbarWarper />
    </Suspense>
  );
};

export { routesList };
export default App;
